import Fuse from 'fuse.js'

/**
 * Normalize a client name for fuzzy comparison: lowercase, drop common business
 * suffixes (Inc / LLC / Ltd / Body & Paint / Automotive / …) and collapse any
 * punctuation/whitespace runs into single spaces. The result is what fuse.js
 * indexes against so "Schneider Body & Paint, LLC" and "schneider body paint"
 * compare cleanly.
 */
const SUFFIX_NOISE = /\b(inc|incorporated|llc|l\.l\.c|ltd|co|corp|corporation|company|body|paint|automotive|auto|the)\b/g
const PUNCT_OR_WS = /[\s\-_,.&()/'"]+/g

/**
 * Insurer equivalent of SUFFIX_NOISE. Carrier names are dominated by generic insurance words, so
 * without stripping them "STATE FARM INSURANCE COMPANIES" scores against "Auto-Owners Insurance"
 * on the shared word "insurance" and outranks the actual State Farm row. Identity-bearing words
 * (american, national, general, farm, ...) are deliberately kept so siblings stay distinct.
 */
const INSURER_NOISE =
  /\b(insurance|insurances|insurer|insurers|ins|insco|company|companies|comp|co|corp|corporation|group|grp|holdings|holding|mutual|casualty|indemnity|assurance|inc|incorporated|llc|ltd|the|of|and)\b/g

const normalizeWith = (value, noise) => {
  if (!value) return ''
  return String(value).toLowerCase().replace(noise, ' ').replace(PUNCT_OR_WS, ' ').trim()
}

/**
 * Fuzzy-match a backend-extracted client name against the user's client list
 * using fuse.js. Returns the subset of clients above the similarity threshold,
 * best match first, each annotated with the raw fuse score.
 *
 * Threshold maps to fuse's distance score (0 = perfect, 1 = anything). 0.6 is
 * the product default — loose enough to catch reasonable typos / abbreviations
 * without flooding with unrelated names.
 */
export const fuzzyMatchClients = (extractedName, clients = [], { threshold = 0.6 } = {}) =>
  fuzzyMatch(extractedName, clients, SUFFIX_NOISE, threshold)

/**
 * Same matcher for insurance companies, differing only in which filler words are stripped.
 * Use when the backend's canonical resolve (CAR-3767) came back empty.
 */
export const fuzzyMatchInsurers = (extractedName, insurers = [], { threshold = 0.6 } = {}) =>
  fuzzyMatch(extractedName, insurers, INSURER_NOISE, threshold)

/**
 * The same carrier under two spellings: equal once case, punctuation and the generic insurance words are dropped
 * ("State Farm Insurance Companies" = "State Farm", "Hanover Insurance Group" = "Hanover", "Bear River Mutual" =
 * "Bear River Insurance"). Strict equality, never a fuzzy guess: "Tennessee Farmers Insurance Cos." is not
 * "Farmers", and "Farm Bureau" is not "State Farm". Used to pair an insurer with its national agreement.
 */
export const sameCarrier = (a, b) => {
  const left = normalizeWith(a, INSURER_NOISE)
  return Boolean(left) && left === normalizeWith(b, INSURER_NOISE)
}

/** Case/whitespace-insensitive exact lookup of a client / insurer by display name. */
export const matchByName = (list, name) => {
  if (!name || name === '—') return undefined
  const norm = String(name).trim().toLowerCase()
  return list?.find((c) => (c.name || c.value || '').trim().toLowerCase() === norm)
}

/** Fuse returns a hit whenever anything overlaps, and its own `threshold` does not filter the
 *  score it reports, so weak coincidental hits come back alongside real ones. Measured against the
 *  live client list: genuine variants score 0.000–0.480 ("ST CLOUD COLLISION CENTER" → "ST. CLOUD
 *  COLLISION" is the worst real one), while matches on a single shared word score 0.672+
 *  ("O'RIELLY COLLISION CENTER" → "AF COLLISION"). 0.55 sits in that gap. */
export const MAX_FUZZY_SCORE = 0.55

/** Match an extracted name against a lookup list the way New Quote does on the single-quote path
 *  (CAR-3459): exact name first, then the best fuzzy hit — but only one confident enough to be
 *  worth pre-selecting. Below the bar the caller leaves the field unset, which renders the extracted
 *  name as the dropdown placeholder. `fuzzy` is the matcher tuned for that list's filler words
 *  (shop suffixes vs insurance suffixes). Used for insurers wherever the backend's canonical
 *  resolve (CAR-3767) came back empty: the queue's bulk quote, Estimate Summary and New Quote. */
export const matchWithFuzzy = (list, name, fuzzy) => {
  if (!name || name === '—') return undefined
  const exact = matchByName(list, name)
  if (exact) return exact
  const best = fuzzy(name, list)[0]
  return best && best.fuzzyScore <= MAX_FUZZY_SCORE && sharesADistinctiveWord(name, best.name || best.value) ? best : undefined
}

/** Words that name a kind of business, not a business: two shops sharing only one of these are not the
 *  same shop, however well fuse scores them ("Gerber Collision" vs "Walser Collision" scores 0.375). */
const GENERIC_WORDS = new Set(
  (
    'inc incorporated llc ltd co corp corporation company companies body paint automotive auto autobody the and of ' +
    'collision center centre repair repairs shop shops service services group insurance insurances insurer insurers ' +
    'ins insco comp grp holdings holding mutual casualty indemnity assurance exchange'
  ).split(' ')
)

/** A confident fuzzy hit must also share a distinctive word with the extracted name: a token of 3+
 *  letters that is not a generic business word, equal on both sides or (4+ letters) with the same first
 *  four letters, so a typo or "Northstar" against "North Star" still passes. Exported for tests. */
export const sharesADistinctiveWord = (a, b) => {
  const tokens = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(PUNCT_OR_WS, ' ')
      .split(' ')
      .filter((t) => t.length >= 3 && !GENERIC_WORDS.has(t))
  const left = tokens(a)
  const right = tokens(b)
  return left.some((x) => right.some((y) => x === y || (x.length >= 4 && y.length >= 4 && x.slice(0, 4) === y.slice(0, 4))))
}

const fuzzyMatch = (extractedName, candidates, noise, threshold) => {
  const query = normalizeWith(extractedName, noise)
  if (!query || !Array.isArray(candidates) || candidates.length === 0) return []

  const indexed = candidates
    .map((c) => ({ client: c, normalized: normalizeWith(c.name || c.value || '', noise) }))
    .filter((row) => row.normalized)

  if (indexed.length === 0) return []

  const fuse = new Fuse(indexed, {
    keys: ['normalized'],
    threshold,
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 2
  })

  return fuse.search(query).map(({ item, score }) => ({ ...item.client, fuzzyScore: score }))
}
